import React, { useState, useEffect, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Battery,
  Sparkles,
  Calculator,
  ShieldCheck,
  Award,
  MapPin,
  ArrowRight,
  Check,
  Phone,
  MessageCircle,
  ChevronDown,
  Sun,
  Home as HomeIcon,
  Zap,
  TrendingUp,
  Star,
  Clock,
} from "lucide-react";
import { Slider } from "../components/ui/slider";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import api from "../lib/api";
import { track } from "../lib/track";
import GoogleReviews from "../components/GoogleReviews";

const HERO_IMG = "/images/batteries/tesla-powerwall-3.png";
const PHONE_HREF = "tel:+31852502086";
const PHONE_LABEL = "+31 85 250 2086";
const WHATSAPP_HREF =
  "https://wa.me/31852502086?text=Hallo%20Helios%20Bright%2C%20ik%20heb%20interesse%20in%20een%20thuisbatterij.";

/* --------------------------------- NAV ---------------------------------- */
const MinimalNav = () => {
  const scrollTo = (id) => (e) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  return (
    <header
      className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-slate-200/60"
      data-testid="ads-nav"
    >
      <div className="max-w-6xl mx-auto px-5 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/thuisbatterij" className="flex items-center gap-2" data-testid="ads-nav-logo">
          <span className="w-9 h-9 rounded-full gradient-pink flex items-center justify-center text-white">
            <Sun size={18} />
          </span>
          <span className="font-heading font-black text-lg tracking-tight text-navy-900">
            Helios Bright
          </span>
        </Link>
        <nav className="hidden md:flex items-center gap-7 text-sm font-semibold text-slate-700">
          <a href="#calculator" onClick={scrollTo("calculator")} className="hover:text-brand transition-colors">
            Bereken besparing
          </a>
          <a href="#reviews" onClick={scrollTo("reviews")} className="hover:text-brand transition-colors">
            Reviews
          </a>
          <a href="#faq" onClick={scrollTo("faq")} className="hover:text-brand transition-colors">
            Veelgestelde vragen
          </a>
        </nav>
        <a
          href={PHONE_HREF}
          onClick={() => track.phoneClick()}
          className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 rounded-full border border-slate-200 text-navy-900 font-bold text-sm hover:border-navy-900 transition-colors"
          data-testid="ads-nav-phone"
        >
          <Phone size={14} className="text-brand" />
          <span className="hidden lg:inline">{PHONE_LABEL}</span>
          <span className="hidden sm:inline lg:hidden">Bel</span>
        </a>
      </div>
    </header>
  );
};

/* -------------------------------- HERO ---------------------------------- */
const Hero = () => {
  const scrollTo = (id) => (e) => {
    e.preventDefault();
    track.ctaClick(id === "calculator" ? "hero_calc" : "hero_advice");
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const usps = [
    "Gratis berekening",
    "Inclusief installatie",
    "Persoonlijk advies",
    "Geen verplichtingen",
  ];
  return (
    <section
      className="relative overflow-hidden bg-white pt-10 lg:pt-16 pb-14 lg:pb-20"
      data-testid="ads-hero"
    >
      {/* Ambient gradients */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <div className="absolute -top-32 -right-32 w-[640px] h-[640px] rounded-full bg-[radial-gradient(circle,rgba(255,47,125,0.18)_0%,rgba(255,47,125,0)_60%)]" />
        <div className="absolute bottom-0 -left-32 w-[520px] h-[520px] rounded-full bg-[radial-gradient(circle,rgba(255,47,125,0.10)_0%,rgba(255,47,125,0)_60%)]" />
      </div>

      <div className="relative max-w-6xl mx-auto px-5 lg:px-8 grid lg:grid-cols-12 gap-10 items-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="lg:col-span-7"
        >
          <span
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-brand-soft text-brand text-[11px] font-bold uppercase tracking-[0.16em]"
            data-testid="ads-hero-pill"
          >
            <MapPin size={12} /> Actief in Groningen & Noord-Nederland
          </span>
          <h1
            className="mt-5 font-heading font-black tracking-tighter text-navy-900 text-balance
                       text-4xl leading-[1.05] sm:text-5xl lg:text-[4.25rem] lg:leading-[1.02]"
            data-testid="ads-hero-headline"
          >
            Thuisbatterij kopen in <span className="gradient-pink-text">Groningen</span> &
            Noord-Nederland
          </h1>
          <p className="mt-5 text-slate-600 text-lg leading-relaxed max-w-xl">
            Gebruik meer van je eigen zonnestroom en bereid je voor op het einde van de salderingsregeling.
          </p>

          <div className="mt-7 flex flex-wrap gap-3">
            <a
              href="#calculator"
              onClick={scrollTo("calculator")}
              className="btn-pink group"
              data-testid="ads-hero-cta-primary"
            >
              <Calculator size={16} />
              Bereken mijn besparing
              <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" />
            </a>
            <a
              href="#lead"
              onClick={scrollTo("lead")}
              className="btn-ghost"
              data-testid="ads-hero-cta-secondary"
            >
              Vraag gratis advies aan
            </a>
          </div>

          <ul className="mt-7 grid grid-cols-2 sm:flex flex-wrap gap-x-5 gap-y-2">
            {usps.map((u) => (
              <li key={u} className="flex items-center gap-2 text-sm text-slate-700 font-semibold">
                <span className="w-5 h-5 rounded-full bg-brand-soft text-brand flex items-center justify-center flex-shrink-0">
                  <Check size={11} strokeWidth={3} />
                </span>
                {u}
              </li>
            ))}
          </ul>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="lg:col-span-5 relative"
        >
          <div className="relative aspect-[4/5] w-full max-w-md mx-auto">
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="w-[75%] h-[75%] rounded-full bg-[radial-gradient(circle,rgba(255,47,125,0.28)_0%,rgba(255,47,125,0)_65%)]" />
            </div>
            <motion.img
              src={HERO_IMG}
              alt="Premium thuisbatterij"
              loading="eager"
              fetchPriority="high"
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
              className="relative z-10 mx-auto w-[80%] h-full object-contain drop-shadow-[0_40px_50px_rgba(15,23,42,0.20)]"
            />
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="absolute top-6 left-2 z-20 flex items-center gap-2 px-3 py-2 rounded-2xl bg-white/95 backdrop-blur-md border border-slate-200 shadow-lg"
            >
              <span className="w-8 h-8 rounded-lg gradient-pink text-white flex items-center justify-center">
                <ShieldCheck size={15} />
              </span>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Garantie</div>
                <div className="font-heading font-black text-navy-900 text-sm leading-tight">Tot 10 jaar</div>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.65 }}
              className="absolute bottom-6 right-2 z-20 flex items-center gap-2 px-3 py-2 rounded-2xl bg-navy-900 text-white shadow-lg"
            >
              <Award size={14} className="text-brand" />
              <span className="text-xs font-bold uppercase tracking-wider">Premium A-merken</span>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

/* ----------------------------- CALCULATOR ------------------------------- */
const CalcSlider = ({ label, value, onChange, min, max, step, display, testid }) => (
  <div className="mt-6 first:mt-0" data-testid={`${testid}-row`}>
    <div className="flex items-center justify-between mb-3">
      <label className="text-sm font-semibold text-navy-900">{label}</label>
      <span className="text-sm font-bold text-brand tabular-nums">{display}</span>
    </div>
    <Slider value={value} onValueChange={onChange} min={min} max={max} step={step} data-testid={testid} />
  </div>
);

const Metric = ({ label, value, accent = false, loading }) => (
  <div className={`rounded-2xl p-4 ${accent ? "bg-white/10 border border-white/20" : "bg-white/5 border border-white/10"}`}>
    <div className="text-[10px] uppercase tracking-widest text-slate-300 font-bold">{label}</div>
    <div
      className={`font-heading font-black tabular-nums mt-1.5 leading-none ${accent ? "text-4xl gradient-pink-text" : "text-2xl text-white"} ${loading ? "opacity-60" : "opacity-100"} transition-opacity`}
    >
      {value}
    </div>
  </div>
);

const CalculatorSection = () => {
  const [panels, setPanels] = useState([10]);
  const [usage, setUsage] = useState([4000]);
  const [cost, setCost] = useState([180]);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "", postcode: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const startedRef = useRef(false);

  const payload = useMemo(
    () => ({ panels: panels[0], annual_usage_kwh: usage[0], monthly_cost: cost[0] }),
    [panels, usage, cost]
  );

  // fire calc_started once when user first moves any slider
  useEffect(() => {
    if (startedRef.current) return;
    if (panels[0] !== 10 || usage[0] !== 4000 || cost[0] !== 180) {
      startedRef.current = true;
      track.calcStarted(payload);
    }
  }, [panels, usage, cost, payload]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const id = setTimeout(async () => {
      try {
        const { data } = await api.post("/calculator/estimate", payload);
        if (!cancelled) {
          setResult(data);
          track.calcCompleted({
            annual_savings: data.estimated_annual_savings,
            recommended_kwh: data.recommended_battery_kwh,
          });
        }
      } catch (e) {
        if (!cancelled) setResult(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(id);
    };
  }, [payload]);

  const annual = result ? `€ ${Number(result.estimated_annual_savings).toLocaleString("nl-NL")}` : "—";
  const monthly = result ? `€ ${Number(result.monthly_savings).toLocaleString("nl-NL")}` : "—";
  const recommended = result ? `${Number(result.recommended_battery_kwh)} kWh` : "—";
  const payback = result ? `${result.payback_years} jaar` : "—";

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const body = {
        name: form.name,
        email: form.email,
        phone: form.phone,
        message: `Postcode: ${form.postcode}`,
        source: "ads_thuisbatterij",
        type: "thuisbatterij",
        data: {
          panels: payload.panels,
          annual_usage_kwh: payload.annual_usage_kwh,
          monthly_cost: payload.monthly_cost,
          postcode: form.postcode,
          ...(result || {}),
        },
      };
      await api.post("/leads", body);
      track.leadSubmitted({ value: result?.estimated_annual_savings || 0 });
      setDone(true);
      toast.success("Bedankt! Onze adviseur belt je binnen 24 uur.");
    } catch (err) {
      toast.error("Er ging iets mis. Probeer het opnieuw.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="calculator" className="py-16 lg:py-24 bg-slate-50/60" data-testid="ads-calculator">
      <div className="max-w-6xl mx-auto px-5 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand mb-3">
            Bespaarcheck
          </p>
          <h2 className="font-heading text-4xl md:text-5xl font-black tracking-tighter text-navy-900 text-balance">
            Bereken jouw besparing in <span className="gradient-pink-text">30 seconden</span>
          </h2>
          <p className="mt-4 text-slate-600 leading-relaxed">
            Vul in en zie direct wat een thuisbatterij jou oplevert.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-6">
          {/* Inputs */}
          <div className="lg:col-span-3 bg-white rounded-3xl border border-slate-200 shadow-[0_20px_60px_-20px_rgba(15,23,42,0.15)] p-7 lg:p-9">
            <CalcSlider
              label="Aantal zonnepanelen"
              value={panels}
              onChange={setPanels}
              min={0}
              max={40}
              step={1}
              display={`${panels[0]}`}
              testid="ads-calc-panels"
            />
            <CalcSlider
              label="Jaarlijks stroomverbruik"
              value={usage}
              onChange={setUsage}
              min={1000}
              max={10000}
              step={100}
              display={`${usage[0].toLocaleString("nl-NL")} kWh`}
              testid="ads-calc-usage"
            />
            <CalcSlider
              label="Maandelijkse energiekosten"
              value={cost}
              onChange={setCost}
              min={50}
              max={500}
              step={10}
              display={`€ ${cost[0]}`}
              testid="ads-calc-cost"
            />
          </div>

          {/* Results */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="lg:col-span-2 bg-navy-900 text-white rounded-3xl p-7 lg:p-9 relative overflow-hidden"
            data-testid="ads-calc-results"
          >
            <div className="absolute -top-24 -right-16 w-[280px] h-[280px] rounded-full bg-[radial-gradient(circle,rgba(255,47,125,0.35)_0%,rgba(255,47,125,0)_70%)] pointer-events-none" />
            <div className="relative">
              <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-brand mb-3">
                Jouw resultaat
              </p>

              <div className="mb-4">
                <div className="text-[10px] uppercase tracking-widest text-slate-300 font-bold">
                  Geschatte jaarlijkse besparing
                </div>
                <motion.div
                  key={annual}
                  initial={{ scale: 0.95, opacity: 0.6 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.25 }}
                  className={`font-heading font-black text-5xl sm:text-6xl mt-1 gradient-pink-text tabular-nums leading-none ${loading ? "opacity-60" : "opacity-100"} transition-opacity`}
                  data-testid="ads-calc-annual"
                >
                  {annual}
                </motion.div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <Metric label="Aanbevolen capaciteit" value={recommended} loading={loading} />
                <Metric label="Maand" value={monthly} loading={loading} />
                <Metric label="Terugverdientijd" value={payback} loading={loading} />
                <Metric label="Panelen" value={`${panels[0]}`} loading={loading} />
              </div>
            </div>
          </motion.div>
        </div>

        {/* Lead capture */}
        <div id="lead" className="mt-8">
          <AnimatePresence mode="wait">
            {done ? (
              <motion.div
                key="done"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-3xl border border-brand/30 shadow-[0_20px_60px_-20px_rgba(255,47,125,0.25)] p-8 lg:p-10 text-center max-w-2xl mx-auto"
                data-testid="ads-lead-success"
              >
                <div className="w-16 h-16 rounded-full bg-brand-soft mx-auto flex items-center justify-center mb-5">
                  <Check className="text-brand" size={30} strokeWidth={3} />
                </div>
                <h3 className="font-heading text-2xl font-black text-navy-900">
                  Bedankt {form.name.split(" ")[0]}!
                </h3>
                <p className="mt-2 text-slate-600 leading-relaxed">
                  Je persoonlijke berekening is onderweg. Onze adviseur belt je binnen 24 uur op {form.phone}.
                </p>
                <a
                  href={PHONE_HREF}
                  onClick={() => track.phoneClick()}
                  className="btn-pink mt-6"
                  data-testid="ads-lead-success-phone"
                >
                  <Phone size={16} /> Direct bellen
                </a>
              </motion.div>
            ) : (
              <motion.form
                key="form"
                onSubmit={onSubmit}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-3xl border border-slate-200 shadow-[0_20px_60px_-20px_rgba(15,23,42,0.12)] p-6 lg:p-8"
                data-testid="ads-lead-form"
              >
                <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-6">
                  <div>
                    <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-brand">
                      Ontvang je persoonlijke berekening
                    </p>
                    <h3 className="mt-2 font-heading text-2xl lg:text-3xl font-black text-navy-900 tracking-tight">
                      Wil je weten wat dit voor jouw woning betekent?
                    </h3>
                  </div>
                  <span className="inline-flex items-center gap-2 text-xs font-semibold text-slate-500">
                    <ShieldCheck size={14} className="text-brand" /> Gratis en vrijblijvend
                  </span>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <FormField label="Naam" name="name" value={form.name} onChange={onChange} required />
                  <FormField
                    label="Telefoonnummer"
                    name="phone"
                    type="tel"
                    value={form.phone}
                    onChange={onChange}
                    required
                  />
                  <FormField
                    label="E-mailadres"
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={onChange}
                    required
                  />
                  <FormField
                    label="Postcode"
                    name="postcode"
                    value={form.postcode}
                    onChange={onChange}
                    required
                  />
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="mt-6 btn-pink w-full !py-4 disabled:opacity-60"
                  data-testid="ads-lead-submit"
                >
                  {submitting ? "Versturen..." : "Ontvang mijn persoonlijke berekening"}
                  {!submitting && <ArrowRight size={16} />}
                </button>
                <p className="mt-3 text-[11px] text-slate-500 text-center">
                  Door te versturen ga je akkoord met onze privacyverklaring.
                </p>
              </motion.form>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};

const FormField = ({ label, name, type = "text", value, onChange, required }) => (
  <div>
    <label className="text-sm font-semibold text-navy-900 mb-2 block">
      {label}
      {required && <span className="text-brand"> *</span>}
    </label>
    <Input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      required={required}
      className="rounded-xl h-12 border-slate-200 focus-visible:ring-brand"
      data-testid={`ads-field-${name}`}
    />
  </div>
);

/* ----------------------------- WHY HELIOS ------------------------------- */
const WhyHelios = () => {
  const points = [
    { icon: ShieldCheck, title: "Professionele installatie", desc: "Ervaren monteurs met certificering." },
    { icon: Check, title: "Transparante offerte", desc: "Vaste prijs, geen verrassingen achteraf." },
    { icon: Award, title: "Premium A-merken", desc: "Tesla, BYD, Huawei en Enphase." },
    { icon: MapPin, title: "Actief in Noord-Nederland", desc: "Groningen, Friesland en Drenthe." },
  ];
  return (
    <section className="py-16 lg:py-24 bg-white" data-testid="ads-why">
      <div className="max-w-6xl mx-auto px-5 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand mb-3">Vertrouwen</p>
          <h2 className="font-heading text-4xl md:text-5xl font-black tracking-tighter text-navy-900 text-balance">
            Waarom Helios Bright?
          </h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {points.map((p) => (
            <div
              key={p.title}
              className="p-6 rounded-2xl bg-slate-50/70 border border-slate-100 hover:bg-white hover:border-brand/20 hover:shadow-[0_20px_50px_rgba(255,47,125,0.08)] hover:-translate-y-1 transition-all duration-300"
            >
              <span className="w-11 h-11 rounded-xl gradient-pink text-white flex items-center justify-center mb-4">
                <p.icon size={20} />
              </span>
              <h3 className="font-heading font-bold text-lg text-navy-900">{p.title}</h3>
              <p className="text-sm text-slate-600 mt-1.5 leading-relaxed">{p.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ------------------------------- BRANDS --------------------------------- */
const Brands = () => {
  const brands = ["BYD", "Tesla", "Huawei", "Enphase"];
  return (
    <section className="py-14 lg:py-16 bg-slate-50/60 border-y border-slate-200/60" data-testid="ads-brands">
      <div className="max-w-5xl mx-auto px-5 lg:px-8 text-center">
        <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand mb-3">Merken</p>
        <h2 className="font-heading text-3xl md:text-4xl font-black tracking-tighter text-navy-900 text-balance">
          Wij werken met toonaangevende merken
        </h2>
        <div className="mt-9 grid grid-cols-2 sm:grid-cols-4 gap-4">
          {brands.map((b) => (
            <div
              key={b}
              className="h-20 rounded-2xl bg-white border border-slate-200 flex items-center justify-center"
            >
              <span className="font-heading font-black text-2xl md:text-3xl text-navy-900/70 tracking-tight hover:text-brand transition-colors">
                {b}
              </span>
            </div>
          ))}
        </div>
        <p className="mt-7 text-slate-600 max-w-xl mx-auto text-sm leading-relaxed">
          Wij selecteren de oplossing die past bij jouw woning, energieverbruik en bestaande installatie.
        </p>
      </div>
    </section>
  );
};

/* ---------------------- WHY A HOME BATTERY (VISUAL) --------------------- */
const WhyBattery = () => (
  <section className="py-16 lg:py-24 bg-white" data-testid="ads-why-battery">
    <div className="max-w-5xl mx-auto px-5 lg:px-8">
      <div className="text-center max-w-2xl mx-auto mb-12">
        <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand mb-3">Zo werkt het</p>
        <h2 className="font-heading text-4xl md:text-5xl font-black tracking-tighter text-navy-900 text-balance">
          Meer van je eigen zonnestroom gebruiken
        </h2>
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        <div className="p-6 lg:p-8 rounded-3xl border border-slate-200 bg-slate-50/60">
          <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-4">
            Zonder thuisbatterij
          </div>
          <div className="flex items-center justify-between gap-3">
            <FlowIcon icon={Sun} label="Zonnepanelen" />
            <FlowArrow />
            <FlowIcon icon={HomeIcon} label="Huis" />
            <FlowArrow />
            <FlowIcon icon={TrendingUp} label="Overschot naar net" muted />
          </div>
        </div>
        <div className="p-6 lg:p-8 rounded-3xl border border-brand/25 bg-gradient-to-br from-brand-soft/50 to-white shadow-[0_20px_60px_-20px_rgba(255,47,125,0.25)]">
          <div className="text-xs font-bold uppercase tracking-widest text-brand mb-4">Met thuisbatterij</div>
          <div className="flex items-center justify-between gap-3">
            <FlowIcon icon={Sun} label="Zonnepanelen" />
            <FlowArrow accent />
            <FlowIcon icon={HomeIcon} label="Huis" />
            <FlowArrow accent />
            <FlowIcon icon={Battery} label="Batterij" accent />
          </div>
        </div>
      </div>

      <div className="mt-8 grid sm:grid-cols-3 gap-4 max-w-3xl mx-auto">
        {[
          { icon: Zap, text: "Meer eigen verbruik van je zonnestroom" },
          { icon: TrendingUp, text: "Bespaar op stijgende energieprijzen" },
          { icon: ShieldCheck, text: "Klaar voor het einde van de saldering" },
        ].map((b) => (
          <div
            key={b.text}
            className="flex items-start gap-3 p-4 rounded-2xl bg-slate-50/70 border border-slate-100"
          >
            <span className="w-9 h-9 rounded-lg bg-brand-soft text-brand flex items-center justify-center flex-shrink-0">
              <b.icon size={17} />
            </span>
            <span className="text-sm text-navy-900 font-medium">{b.text}</span>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const FlowIcon = ({ icon: Icon, label, accent = false, muted = false }) => (
  <div className="flex flex-col items-center text-center gap-2">
    <span
      className={`w-14 h-14 lg:w-16 lg:h-16 rounded-2xl flex items-center justify-center ${
        accent
          ? "gradient-pink text-white shadow-[0_10px_30px_rgba(255,47,125,0.35)]"
          : muted
          ? "bg-slate-200 text-slate-500"
          : "bg-white border border-slate-200 text-navy-900"
      }`}
    >
      <Icon size={22} />
    </span>
    <span className="text-[11px] font-semibold text-navy-900 leading-tight max-w-[80px]">{label}</span>
  </div>
);

const FlowArrow = ({ accent = false }) => (
  <div className={`flex-shrink-0 ${accent ? "text-brand" : "text-slate-400"}`}>
    <ArrowRight size={18} />
  </div>
);

/* ------------------------------ SALDERING ------------------------------- */
const Saldering = () => {
  const scrollToCalc = (e) => {
    e.preventDefault();
    track.ctaClick("saldering_cta");
    document.getElementById("calculator")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  return (
    <section className="py-16 lg:py-20 bg-slate-50/60" data-testid="ads-saldering">
      <div className="max-w-4xl mx-auto px-5 lg:px-8">
        <div className="rounded-3xl bg-navy-900 text-white p-8 lg:p-12 relative overflow-hidden">
          <div className="absolute -top-24 -right-24 w-[360px] h-[360px] rounded-full bg-[radial-gradient(circle,rgba(255,47,125,0.35)_0%,rgba(255,47,125,0)_70%)] pointer-events-none" />
          <div className="relative">
            <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-brand mb-3">2027</p>
            <h2 className="font-heading text-3xl md:text-4xl font-black tracking-tighter text-balance">
              Wat verandert er vanaf 2027?
            </h2>
            <p className="mt-4 text-slate-300 leading-relaxed max-w-2xl">
              De salderingsregeling stopt vanaf 2027. Stroom die je terug levert aan het net levert
              vanaf dan veel minder op. Zelf gebruiken van je zonnestroom wordt daarom belangrijker
              — een thuisbatterij helpt je die stroom op te slaan voor later.
            </p>
            <a
              href="#calculator"
              onClick={scrollToCalc}
              className="btn-pink mt-6"
              data-testid="ads-saldering-cta"
            >
              <Calculator size={16} /> Bereken wat dit voor mij betekent
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

/* -------------------------------- FAQ ----------------------------------- */
const faqs = [
  {
    q: "Wat kost een thuisbatterij?",
    a: "De prijs hangt af van de gekozen capaciteit en het merk. Systemen beginnen bij circa € 1.499 en lopen op tot € 9.000+ voor een compleet premium systeem inclusief installatie. Wij maken op basis van je situatie een offerte op maat.",
  },
  {
    q: "Welke batterijcapaciteit heb ik nodig?",
    a: "Dat hangt af van je jaarverbruik, het aantal zonnepanelen en je verbruikspatroon. Voor een gemiddeld huishouden in Nederland is 5–10 kWh vaak passend. In de bespaarcheck hierboven zie je direct wat wij voor jou aanbevelen.",
  },
  {
    q: "Heb ik zonnepanelen nodig?",
    a: "Niet per se. Een thuisbatterij kan ook rendabel zijn zonder zonnepanelen als je een dynamisch energiecontract hebt — dan laad je op wanneer stroom goedkoop is en gebruik je die energie op piekmomenten.",
  },
  {
    q: "Wat verandert er vanaf 2027?",
    a: "De salderingsregeling stopt. Terugleveren wordt daardoor veel minder aantrekkelijk. Een thuisbatterij zorgt dat je zoveel mogelijk van je eigen zonnestroom gebruikt in plaats van teruglevert.",
  },
  {
    q: "Hoe lang duurt de installatie?",
    a: "Een standaard installatie duurt gemiddeld één dag. Onze monteurs plaatsen de batterij, sluiten hem aan op je meterkast en configureren de app zodat je direct kunt starten met besparen.",
  },
  {
    q: "Werkt een thuisbatterij bij stroomuitval?",
    a: "Sommige modellen (zoals Tesla Powerwall) hebben een volledige back-up functie waarmee je huis blijft werken bij stroomuitval. Bij andere modellen is dit optioneel. In onze offerte geven we aan wat wél en niet kan.",
  },
];

const FaqItem = ({ q, a, i }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-slate-200 last:border-0">
      <button
        type="button"
        onClick={() => setOpen((c) => !c)}
        className="w-full flex items-center justify-between py-5 text-left"
        aria-expanded={open}
        data-testid={`ads-faq-${i}`}
      >
        <span className="font-heading font-bold text-navy-900 text-base lg:text-lg pr-4">{q}</span>
        <ChevronDown
          size={18}
          className={`text-brand transition-transform duration-300 flex-shrink-0 ${open ? "rotate-180" : ""}`}
        />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <p className="pb-5 pr-8 text-slate-600 leading-relaxed text-sm">{a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const FAQSection = () => (
  <section id="faq" className="py-16 lg:py-24 bg-white" data-testid="ads-faq">
    <div className="max-w-3xl mx-auto px-5 lg:px-8">
      <div className="text-center mb-10">
        <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand mb-3">Vragen</p>
        <h2 className="font-heading text-4xl md:text-5xl font-black tracking-tighter text-navy-900 text-balance">
          Veelgestelde vragen
        </h2>
      </div>
      <div className="rounded-3xl bg-white border border-slate-200 px-6 lg:px-8 shadow-[0_20px_60px_-20px_rgba(15,23,42,0.08)]">
        {faqs.map((f, i) => (
          <FaqItem key={f.q} {...f} i={i} />
        ))}
      </div>
    </div>
  </section>
);

/* --------------------------- FINAL CTA BLOCK ---------------------------- */
const FinalCTA = () => {
  const scrollToCalc = (e) => {
    e.preventDefault();
    track.ctaClick("final_calc");
    document.getElementById("calculator")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  return (
    <section className="py-16 lg:py-24 bg-white" data-testid="ads-final-cta">
      <div className="max-w-4xl mx-auto px-5 lg:px-8">
        <div className="rounded-3xl bg-gradient-to-br from-navy-900 via-navy-900 to-[#1e293b] text-white p-8 lg:p-14 text-center relative overflow-hidden">
          <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[520px] h-[520px] rounded-full bg-[radial-gradient(circle,rgba(255,47,125,0.30)_0%,rgba(255,47,125,0)_65%)] pointer-events-none" />
          <div className="relative">
            <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-brand mb-3">
              Aan de slag
            </p>
            <h2 className="font-heading text-3xl md:text-5xl font-black tracking-tighter text-balance leading-tight">
              Benieuwd welke thuisbatterij bij jouw woning past?
            </h2>
            <p className="mt-5 text-slate-300 max-w-xl mx-auto leading-relaxed">
              Bereken gratis jouw situatie en ontvang persoonlijk advies van Helios Bright.
            </p>
            <div className="mt-8 flex flex-wrap gap-3 justify-center">
              <a
                href="#calculator"
                onClick={scrollToCalc}
                className="btn-pink"
                data-testid="ads-final-cta-primary"
              >
                <Calculator size={16} /> Bereken mijn besparing
              </a>
              <a
                href={PHONE_HREF}
                onClick={() => track.phoneClick()}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/20 text-white font-bold hover:bg-white/10 transition-colors"
                data-testid="ads-final-cta-phone"
              >
                <Phone size={16} /> Bel Helios Bright
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* --------------------------- STICKY MOBILE CTA -------------------------- */
const StickyMobileCTA = () => {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 500);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  const onClick = (e) => {
    e.preventDefault();
    track.ctaClick("sticky_mobile");
    document.getElementById("calculator")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 80, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 80, opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="lg:hidden fixed bottom-0 inset-x-0 z-30 p-3 bg-white/95 backdrop-blur-xl border-t border-slate-200"
          data-testid="ads-sticky-cta"
        >
          <a
            href="#calculator"
            onClick={onClick}
            className="btn-pink w-full !py-4"
            data-testid="ads-sticky-cta-btn"
          >
            <Calculator size={16} /> Bereken mijn besparing
          </a>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

/* ------------------------------- FOOTER --------------------------------- */
const MiniFooter = () => (
  <footer className="bg-slate-50/60 border-t border-slate-200 py-8">
    <div className="max-w-6xl mx-auto px-5 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-slate-600">
      <div className="flex items-center gap-2 font-heading font-bold text-navy-900">
        <span className="w-7 h-7 rounded-full gradient-pink flex items-center justify-center text-white">
          <Sun size={14} />
        </span>
        Helios Bright
      </div>
      <div className="flex items-center gap-5">
        <a href={PHONE_HREF} onClick={() => track.phoneClick()} className="hover:text-brand transition-colors">
          {PHONE_LABEL}
        </a>
        <Link to="/" className="hover:text-brand transition-colors">
          Volledige website
        </Link>
      </div>
    </div>
  </footer>
);

/* -------------------------- WHATSAPP FLOATING --------------------------- */
const WhatsAppFab = () => (
  <a
    href={WHATSAPP_HREF}
    target="_blank"
    rel="noopener noreferrer"
    onClick={() => track.whatsappClick()}
    className="fixed bottom-24 lg:bottom-6 right-6 z-30 w-14 h-14 rounded-full flex items-center justify-center text-white shadow-2xl transition-transform hover:scale-110"
    style={{ background: "#25D366" }}
    aria-label="WhatsApp"
    data-testid="ads-whatsapp"
  >
    <MessageCircle className="w-7 h-7" />
  </a>
);

/* --------------------------------- PAGE --------------------------------- */
const AdsThuisbatterij = () => {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = "Thuisbatterij Groningen & Noord-Nederland | Helios Bright";
    let meta = document.querySelector('meta[name="description"]');
    const prevDesc = meta ? meta.getAttribute("content") : "";
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    meta.setAttribute(
      "content",
      "Thuisbatterij kopen in Groningen en Noord-Nederland. Gebruik meer van je eigen zonnestroom en bereken gratis jouw besparing. Premium A-merken, professionele installatie en persoonlijk advies van Helios Bright."
    );
    return () => {
      document.title = prevTitle;
      if (meta && prevDesc) meta.setAttribute("content", prevDesc);
    };
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="ads-landing">
      <MinimalNav />
      <main>
        <Hero />
        <CalculatorSection />
        <WhyHelios />
        <Brands />
        <WhyBattery />
        <Saldering />
        <section id="reviews">
          <GoogleReviews />
        </section>
        <FAQSection />
        <FinalCTA />
      </main>
      <MiniFooter />
      <WhatsAppFab />
      <StickyMobileCTA />
    </div>
  );
};

export default AdsThuisbatterij;
